import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import {
  clampCaptionSize,
  drawVideoCaption,
  ensureCaptionFontsLoaded,
  hasCaptionText,
  type VideoCaption,
  type VideoCaptionBounds,
} from '../../lib/videoCaption';

interface VideoCaptionLayerProps {
  caption: VideoCaption | undefined;
  /** CSS size of the crop frame the layer sits on. */
  width: number;
  height: number;
  onChange: (next: VideoCaption) => void;
  selected: boolean;
  onSelectedChange: (selected: boolean) => void;
  disabled?: boolean;
}

type Pt = { x: number; y: number };

type Gesture =
  | { mode: 'move'; start: VideoCaption; origin: Pt }
  | { mode: 'handle'; start: VideoCaption; origin: Pt }
  | { mode: 'pinch'; start: VideoCaption; startDist: number; startCenter: Pt };

const mid = (a: Pt, b: Pt): Pt => ({ x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 });
const dist = (a: Pt, b: Pt) => Math.hypot(a.x - b.x, a.y - b.y);
const clamp01 = (n: number) => Math.min(1, Math.max(0, n));

/**
 * Canvas overlay that renders the caption exactly as the export will, plus
 * iOS-style controls: tap to select, drag to move, pinch or drag the corner
 * handle to resize.
 */
export default function VideoCaptionLayer({
  caption,
  width,
  height,
  onChange,
  selected,
  onSelectedChange,
  disabled = false,
}: VideoCaptionLayerProps) {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const [bounds, setBounds] = useState<VideoCaptionBounds | null>(null);
  const [fontsReady, setFontsReady] = useState(false);
  const pointers = useRef(new Map<number, Pt>());
  const gesture = useRef<Gesture | null>(null);
  const captionRef = useRef(caption);
  captionRef.current = caption;

  useEffect(() => {
    let cancelled = false;
    void ensureCaptionFontsLoaded().then(() => {
      if (!cancelled) setFontsReady(true);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  // Redraw whenever the caption, frame, or fonts change.
  useLayoutEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || width <= 0 || height <= 0) return;
    const dpr = Math.min(3, window.devicePixelRatio || 1);
    canvas.width = Math.round(width * dpr);
    canvas.height = Math.round(height * dpr);
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    ctx.clearRect(0, 0, width, height);
    if (!hasCaptionText(caption)) {
      setBounds(null);
      return;
    }
    setBounds(drawVideoCaption(ctx, caption, width, height));
  }, [caption, width, height, fontsReady]);

  // Deselect when tapping anywhere outside the caption UI.
  useEffect(() => {
    if (!selected) return;
    const onDown = (e: PointerEvent) => {
      const target = e.target as HTMLElement | null;
      if (target?.closest?.('[data-video-caption-ui]')) return;
      onSelectedChange(false);
    };
    document.addEventListener('pointerdown', onDown, true);
    return () => document.removeEventListener('pointerdown', onDown, true);
  }, [selected, onSelectedChange]);

  const frameRect = useCallback(() => canvasRef.current?.getBoundingClientRect() ?? null, []);

  const begin = (e: React.PointerEvent<HTMLDivElement>, mode: 'move' | 'handle') => {
    const cap = captionRef.current;
    if (disabled || !cap) return;
    e.stopPropagation();
    e.preventDefault();
    try {
      e.currentTarget.setPointerCapture(e.pointerId);
    } catch {
      /* pointer already gone — fall back to bubbling events */
    }
    pointers.current.set(e.pointerId, { x: e.clientX, y: e.clientY });
    onSelectedChange(true);

    const pts = [...pointers.current.values()];
    if (pts.length >= 2) {
      gesture.current = {
        mode: 'pinch',
        start: cap,
        startDist: Math.max(1, dist(pts[0], pts[1])),
        startCenter: mid(pts[0], pts[1]),
      };
      return;
    }
    gesture.current = { mode, start: cap, origin: { x: e.clientX, y: e.clientY } };
  };

  const move = (e: React.PointerEvent<HTMLDivElement>) => {
    const g = gesture.current;
    const cap = captionRef.current;
    if (!g || !cap || !pointers.current.has(e.pointerId)) return;
    pointers.current.set(e.pointerId, { x: e.clientX, y: e.clientY });
    const rect = frameRect();
    if (!rect || rect.width <= 0 || rect.height <= 0) return;

    if (g.mode === 'pinch') {
      const pts = [...pointers.current.values()];
      if (pts.length < 2) return;
      const centre = mid(pts[0], pts[1]);
      onChange({
        ...cap,
        size: clampCaptionSize(g.start.size * (dist(pts[0], pts[1]) / g.startDist)),
        x: clamp01(g.start.x + (centre.x - g.startCenter.x) / rect.width),
        y: clamp01(g.start.y + (centre.y - g.startCenter.y) / rect.height),
      });
      return;
    }

    if (g.mode === 'move') {
      onChange({
        ...cap,
        x: clamp01(g.start.x + (e.clientX - g.origin.x) / rect.width),
        y: clamp01(g.start.y + (e.clientY - g.origin.y) / rect.height),
      });
      return;
    }

    // Corner handle: scale by distance from the block centre.
    const cx = rect.left + g.start.x * rect.width;
    const cy = rect.top + g.start.y * rect.height;
    const startDist = Math.max(8, Math.hypot(g.origin.x - cx, g.origin.y - cy));
    const nowDist = Math.hypot(e.clientX - cx, e.clientY - cy);
    onChange({ ...cap, size: clampCaptionSize(g.start.size * (nowDist / startDist)) });
  };

  const end = (e: React.PointerEvent<HTMLDivElement>) => {
    pointers.current.delete(e.pointerId);
    try {
      e.currentTarget.releasePointerCapture(e.pointerId);
    } catch {
      /* not captured */
    }
    const cap = captionRef.current;
    if (pointers.current.size === 0 || !cap) {
      gesture.current = null;
      return;
    }
    // A finger lifted mid-pinch: carry on as a move from the remaining finger.
    const rest = [...pointers.current.values()][0];
    gesture.current = { mode: 'move', start: cap, origin: { ...rest } };
  };

  const showControls = selected && !!bounds && !disabled;
  const handleSize = 22;

  return (
    <>
      <canvas
        ref={canvasRef}
        className="absolute inset-0 z-10 pointer-events-none"
        style={{ width, height }}
        aria-hidden
      />
      {bounds && !disabled ? (
        <div
          data-video-caption-ui
          role="button"
          aria-label="Caption — drag to move, pinch to resize"
          tabIndex={-1}
          onPointerDown={(e) => begin(e, 'move')}
          onPointerMove={move}
          onPointerUp={end}
          onPointerCancel={end}
          className="absolute z-20"
          style={{
            left: bounds.x - 6,
            top: bounds.y - 6,
            width: bounds.width + 12,
            height: bounds.height + 12,
            touchAction: 'none',
            cursor: 'move',
            borderRadius: 8,
            outline: showControls ? '1.5px dashed rgba(255,255,255,0.95)' : 'none',
            boxShadow: showControls ? '0 0 0 1px rgba(0,0,0,0.45)' : 'none',
            WebkitTapHighlightColor: 'transparent',
          }}
        >
          {showControls ? (
            <div
              data-video-caption-ui
              onPointerDown={(e) => begin(e, 'handle')}
              onPointerMove={move}
              onPointerUp={end}
              onPointerCancel={end}
              className="absolute flex items-center justify-center rounded-full bg-white shadow-md"
              style={{
                width: handleSize,
                height: handleSize,
                right: -handleSize / 2,
                bottom: -handleSize / 2,
                cursor: 'nwse-resize',
                touchAction: 'none',
                border: '1.5px solid rgba(0,0,0,0.25)',
              }}
              aria-label="Resize caption"
            >
              <svg width="10" height="10" viewBox="0 0 10 10" aria-hidden>
                <path
                  d="M1 9 L9 1 M5 9 L9 5"
                  stroke="#111"
                  strokeWidth="1.6"
                  strokeLinecap="round"
                  fill="none"
                />
              </svg>
            </div>
          ) : null}
        </div>
      ) : null}
    </>
  );
}
